import { EditorSection } from '@/components/plot/editor/EditorSection';
import { useFigureCompositionStore, type JournalTemplateId } from '@/store/figure-composition';
import { FigureTitle } from '../FigureTitle';
import { FigureCaption } from '../FigureCaption';
import { TemplatePresetSelector } from '../TemplatePresetSelector';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { journalTemplatePresets, getJournalTemplate } from '@/lib/journalTemplates';
import { Card } from '@/components/ui/card';
import { Info } from 'lucide-react';

export function FigureLayoutTab() {
  const currentComposition = useFigureCompositionStore((state) => state.currentComposition);
  const setTheme = useFigureCompositionStore((state) => state.setTheme);
  const setCanvasSize = useFigureCompositionStore((state) => state.setCanvasSize);
  const setGridSettings = useFigureCompositionStore((state) => state.setGridSettings);
  const updatePanel = useFigureCompositionStore((state) => state.updatePanel);
  const addPanel = useFigureCompositionStore((state) => state.addPanel);
  const removePanel = useFigureCompositionStore((state) => state.removePanel);
  if (!currentComposition) return null;

  const handleJournalTemplateChange = (id: JournalTemplateId | 'default') => {
    try {
      if (!id || id === 'default') {
        setTheme({
          journalTemplate: undefined,
        });
        return;
      }
      
      const template = journalTemplatePresets.find((t) => t.id === id);
      if (!template) {
        console.error(`Journal template "${id}" not found`);
        return;
      }

      const typography = template.typography ?? {};
      let fontFamily = 'Arial';
      if (typography.fontFamily) {
        if (Array.isArray(typography.fontFamily) && typography.fontFamily.length > 0) {
          fontFamily = typography.fontFamily[0];
        } else if (typeof typography.fontFamily === 'string') {
          fontFamily = typography.fontFamily;
        }
      }

      const SCREEN_SCALE_FACTOR = 1.8;
      const fontSizePt =
        typeof typography.fontSizePt_min === 'number'
          ? typography.fontSizePt_min
          : Array.isArray(typography.fontSizePt_range) && typography.fontSizePt_range.length > 0
          ? typography.fontSizePt_range[0]
          : 8;
      const fontSize = Math.max(10, Math.round(fontSizePt * SCREEN_SCALE_FACTOR));

      let colorMode: 'RGB' | 'CMYK' = 'RGB';
      if (template.colorPolicy?.mode === 'CMYK' || template.colorPolicy?.mode === 'CMYK_or_RGB') {
        colorMode = 'CMYK';
      } else if (template.colorPolicy?.mode === 'RGB') {
        colorMode = 'RGB';
      }

      setTheme({
        journalTemplate: id,
        fontFamily,
        fontSize,
        colorMode,
      });

      const sizePolicy = template.sizePolicy;
      if (sizePolicy) {
        if (sizePolicy.mode === 'columns' && Array.isArray(sizePolicy.widths_mm) && sizePolicy.widths_mm.length > 0) {
          const widthMm = Math.max(...sizePolicy.widths_mm);
          const heightMm = sizePolicy.maxHeight_mm ?? 234;
          setCanvasSize({ width: widthMm, height: heightMm, unit: 'mm' });
        } else if (sizePolicy.mode === 'range' && Array.isArray(sizePolicy.width_mm_range) && sizePolicy.width_mm_range.length === 2) {
          const widthMm = sizePolicy.width_mm_range[1];
          const heightMm = sizePolicy.maxHeight_mm ?? 234;
          setCanvasSize({ width: widthMm, height: heightMm, unit: 'mm' });
        } else if (sizePolicy.mode === 'presets' && sizePolicy.presets_cm?.large) {
          const [wCm, hCm] = sizePolicy.presets_cm.large;
          setCanvasSize({ width: wCm * 10, height: hCm * 10, unit: 'mm' });
        }
      }
    } catch (error) {
      console.error('Failed to apply journal template:', error);
    }
  };

  const handleQuickPreset = (rows: number, cols: number) => {
    // Create panel layout for the grid (rows × cols)
    // Each panel takes equal space: width = 12/cols, height = 12/rows
    const panelWidth = 12 / cols;
    const panelHeight = 12 / rows;
    const totalPanels = rows * cols;
    
    // Preserve existing plot assignments if we have the same number of panels
    const existingPanels = currentComposition.panels;
    const existingPlots = existingPanels
      .filter((p) => p.plotId)
      .map((p) => ({ plotId: p.plotId, plotVersionId: p.plotVersionId }));
    
    // Remove excess panels if we're reducing the count
    if (existingPanels.length > totalPanels) {
      const panelsToRemove = existingPanels.slice(totalPanels);
      panelsToRemove.forEach((panel) => {
        removePanel(panel.id);
      });
    }
    
    // Create or update panels for the grid layout
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const index = row * cols + col;
        const x = col * panelWidth;
        const y = row * panelHeight;
        
        if (index < existingPanels.length) {
          // Update existing panel position and size
          updatePanel(existingPanels[index].id, {
            x,
            y,
            w: panelWidth,
            h: panelHeight,
          });
        } else {
          // Create new panel
          const plotAssignment = existingPlots.length > index 
            ? { 
                plotId: existingPlots[index].plotId, 
                plotVersionId: existingPlots[index].plotVersionId 
              }
            : { plotId: '', plotVersionId: undefined };
          
          addPanel({
            x,
            y,
            w: panelWidth,
            h: panelHeight,
            ...plotAssignment,
          });
        }
      }
    }
    
    // Ensure grid columns match
    setGridSettings({
      cols: 12,
      rowHeight: currentComposition.grid.rowHeight,
    });
  };


  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="space-y-1">
        <h3 className="text-xs font-semibold text-foreground">Layout</h3>
        <p className="text-[11px] text-muted-foreground">
          Set grid, spacing, and panel arrangement for the figure.
        </p>
      </div>

      <EditorSection
        id="figure-details"
        title="Figure details"
        description="Optional title and caption for the whole figure."
      >
        <FigureTitle />
        <FigureCaption />
      </EditorSection>

      <EditorSection
        id="layout-grid"
        title="Layout & grid"
        description="Choose a panel layout and adjust spacing."
        collapsible
      >
        <div className="space-y-3">
          <TemplatePresetSelector />
          <div className="space-y-2">
            <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
              Quick presets
            </p>
            <div className="flex flex-wrap gap-1">
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={() => handleQuickPreset(3, 2)}
              >
                3 × 2
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={() => handleQuickPreset(2, 3)}
              >
                2 × 3
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={() => handleQuickPreset(1, 4)}
              >
                1 × 4
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={() => handleQuickPreset(4, 1)}
              >
                4 × 1
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={() => handleQuickPreset(3, 3)}
              >
                3 × 3
              </Button>
            </div>
          </div>
        </div>
      </EditorSection>

      <EditorSection
        id="journal-template"
        title="Journal template"
        description="Apply journal presets for canvas size, fonts, and color mode."
        collapsible
      >
        <div className="space-y-2">
          <div className="space-y-1">
            <Label className="text-sm text-muted-foreground">Template</Label>
            <Select
              value={currentComposition.theme.journalTemplate ?? 'default'}
              onValueChange={(value) => handleJournalTemplateChange(value as JournalTemplateId | 'default')}
            >
              <SelectTrigger className="mt-1 h-10">
                <SelectValue placeholder="Default" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Default</SelectItem>
                {journalTemplatePresets.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name ?? t.id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Canvas size</Label>
            <div className="text-xs text-muted-foreground px-2 py-1 bg-muted/50 rounded">
              {currentComposition.canvasSize.width} × {currentComposition.canvasSize.height} mm
            </div>
          </div>
          
          {currentComposition.theme.journalTemplate && (() => {
            const template = getJournalTemplate(currentComposition.theme.journalTemplate);
            if (!template) return null;
            
            const typography = template.typography;
            const SCREEN_SCALE_FACTOR = 1.8;
            let baseSizePt = typography.fontSizePt_min || typography.fontSizePt_range?.[0] || 8;
            if (currentComposition.theme.journalTemplate === 'cell' && typography.fontSizePt_range) {
              baseSizePt = typography.fontSizePt_range[1] || typography.fontSizePt_range[0] || baseSizePt;
            }
            const titleSizePx = Math.max(14, Math.round(baseSizePt * 1.3 * SCREEN_SCALE_FACTOR));
            const fontFamily = Array.isArray(typography.fontFamily) 
              ? typography.fontFamily[0] 
              : typography.fontFamily || 'Arial';
            
            return (
              <Card className="p-3 bg-muted/50 border-muted">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Info className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                    <Label className="text-xs font-semibold text-foreground">Typography</Label>
                  </div>
                  <div className="space-y-1.5 pl-5 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Body:</span>
                      <span className="font-medium">{fontFamily}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Title size:</span>
                      <span className="font-medium">{titleSizePx}px</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Recommended print size:</span>
                      <span className="font-medium text-[10px]">
                        {typography.fontSizePt_range 
                          ? `${typography.fontSizePt_range[0]}–${typography.fontSizePt_range[1]}pt`
                          : `${baseSizePt}pt`}
                      </span>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })()}
        </div>
      </EditorSection>
    </div>
  );
}

